import React from "react";
import { Link } from "react-router-dom";
import {
  Users, DollarSign, PackageX, ClipboardList, ArrowUpRight, AlertTriangle, TrendingUp, CalendarClock,
} from "lucide-react";
import { useApp, formatBRL, formatDate } from "../context/AppContext";
import { AvisoAlertaTopo } from "../components/AvisoAlertaTopo";

const StatCard = ({ icon: Icon, label, value, tint, sub }) => (
  <div className="bg-white rounded-xl border border-slate-100 p-5 shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-slate-400 font-medium">{label}</p>
        <p className="text-2xl font-bold text-slate-800 mt-1">{value}</p>
        {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
      </div>
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${tint}`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
  </div>
);

const Dashboard = () => {
  const { residentes, consumos, produtos, estoqueEmpresa, faturas, getProduto, getResidente, alertasReposicao, contasPagar, acessoFinanceiro, residentesAtivos, contasReceber } = useApp();

  const mesAtual = new Date().toISOString().slice(0, 7);
  const consumosMes = consumos.filter((c) => c.data.slice(0, 7) === mesAtual);
  const totalFaturarMes = faturas
    .filter((f) => f.mes === mesAtual)
    .reduce((s, f) => s + f.total, 0);

  const estoqueBaixo = produtos.filter(
    (p) => (estoqueEmpresa[p.id] || 0) < p.estoqueMinimo
  );

  // Alerta de vencimento: contas a pagar pendentes que vencem nos próximos 7 dias (ou vencidas)
  const hojeISO = new Date().toISOString().slice(0, 10);
  const limite = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
  // Semana atual (segunda a domingo)
  const _hoje = new Date(); _hoje.setHours(12, 0, 0, 0);
  const _dow = (_hoje.getDay() + 6) % 7; // 0 = segunda
  const _seg = new Date(_hoje); _seg.setDate(_hoje.getDate() - _dow);
  const _dom = new Date(_seg); _dom.setDate(_seg.getDate() + 6);
  const semanaIni = _seg.toISOString().slice(0, 10);
  const semanaFim = _dom.toISOString().slice(0, 10);
  const contasVencendo = (contasPagar || [])
    .filter((c) => c.status === "pendente" && c.vencimento && c.vencimento >= semanaIni && c.vencimento <= semanaFim)
    .sort((a, b) => (a.vencimento || "").localeCompare(b.vencimento || ""));

  // Alerta de vencimento: faturas de residentes em aberto que vencem nos próximos 7 dias (ou vencidas)
  const faturasVencendo = (faturas || [])
    .filter((f) => !f.paga && !f.cancelada && f.total > 0 && f.vencimento && f.vencimento <= limite)
    .sort((a, b) => (a.vencimento || "").localeCompare(b.vencimento || ""));

  // Recebíveis (outras contas a receber) vencendo nos próximos 7 dias ou vencidos
  const recebiveisVencendo = (contasReceber || [])
    .filter((c) => c.status === "pendente" && c.vencimento && c.vencimento <= limite)
    .sort((a, b) => (a.vencimento || "").localeCompare(b.vencimento || ""));

  const recentes = consumos.slice(0, 6);

  return (
    <div className="space-y-6">
      <AvisoAlertaTopo />
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Dashboard</h1>
        <p className="text-slate-400 text-sm mt-0.5">Visão geral do estoque, consumo e faturamento</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Residentes Ativos" value={residentesAtivos.length} tint="bg-blue-50 text-blue-600" sub="Em internação" />
        <StatCard icon={ClipboardList} label="Consumos no mês" value={consumosMes.length} tint="bg-emerald-50 text-emerald-600" sub="Registros de baixa" />
        <StatCard icon={DollarSign} label="A faturar no mês" value={formatBRL(totalFaturarMes)} tint="bg-violet-50 text-violet-600" sub="Itens do estoque da empresa" />
        <StatCard icon={PackageX} label="Estoque baixo" value={estoqueBaixo.length} tint="bg-amber-50 text-amber-600" sub="Produtos abaixo do mínimo" />
      </div>

      {/* Alerta de vencimento — contas a pagar */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm" data-testid="alerta-vencimento">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h2 className="font-semibold text-slate-800 flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-red-500" /> Vencimentos da Semana <span className="text-xs font-normal text-slate-400">({semanaIni.split("-").reverse().slice(0, 2).join("/")} a {semanaFim.split("-").reverse().slice(0, 2).join("/")})</span>
          </h2>
          <Link to="/financeiro/contas-pagar" className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
            Ver todas <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        <div className="p-4">
          {contasVencendo.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">Nenhuma conta a pagar vence nesta semana.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {contasVencendo.map((c) => {
                const vencida = c.vencimento < hojeISO;
                return (
                  <Link key={c.id} to="/financeiro/contas-pagar" data-testid={`vencimento-conta-${c.id}`}
                    className={`flex items-center justify-between px-4 py-3 rounded-lg border transition-colors ${vencida ? "bg-red-50/60 border-red-100 hover:bg-red-50" : "bg-amber-50/60 border-amber-100 hover:bg-amber-50"}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">{c.descricao}</p>
                      <p className="text-xs text-slate-500 truncate">{c.categoria} · venc. {formatDate(c.vencimento)}</p>
                    </div>
                    <div className="text-right ml-3">
                      <span className={`text-[11px] font-semibold ${vencida ? "text-red-600" : "text-amber-600"}`}>{vencida ? "Vencida" : "A vencer"}</span>
                      <p className="text-sm font-bold text-slate-700">{formatBRL(c.valor)}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Alerta de vencimento — faturas de residentes (mensalidades) */}
      {acessoFinanceiro && (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm" data-testid="alerta-vencimento-faturas">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <h2 className="font-semibold text-slate-800 flex items-center gap-2">
              <CalendarClock className="w-4 h-4 text-red-500" /> Faturas — Vencimentos próximos
            </h2>
            <Link to="/faturas" className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
              Ver todas <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="p-4">
            {faturasVencendo.length === 0 ? (
              <p className="text-sm text-slate-400 py-6 text-center">Nenhuma fatura em aberto vencendo nos próximos 7 dias. 🎉</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {faturasVencendo.map((f) => {
                  const res = getResidente(f.residenteId);
                  const vencida = f.vencimento < hojeISO;
                  return (
                    <Link key={f.key} to="/faturas" data-testid={`vencimento-fatura-${f.key}`}
                      className={`flex items-center justify-between px-4 py-3 rounded-lg border transition-colors ${vencida ? "bg-red-50/60 border-red-100 hover:bg-red-50" : "bg-amber-50/60 border-amber-100 hover:bg-amber-50"}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate">{res?.nome || "Residente"}</p>
                        <p className="text-xs text-slate-500 truncate">Ref. {f.mes} · venc. {formatDate(f.vencimento)}</p>
                      </div>
                      <div className="text-right ml-3">
                        <span className={`text-[11px] font-semibold ${vencida ? "text-red-600" : "text-amber-600"}`}>{vencida ? "Vencida" : "A vencer"}</span>
                        <p className="text-sm font-bold text-slate-700">{formatBRL(f.total)}</p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Alerta de vencimento — outras contas a receber */}
      {acessoFinanceiro && recebiveisVencendo.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm" data-testid="alerta-vencimento-recebiveis">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <h2 className="font-semibold text-slate-800 flex items-center gap-2">
              <CalendarClock className="w-4 h-4 text-emerald-500" /> Recebíveis — Vencimentos próximos
            </h2>
            <Link to="/faturas" className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
              Ver todos <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {recebiveisVencendo.map((c) => {
                const vencida = c.vencimento < hojeISO;
                return (
                  <Link key={c.id} to="/faturas" data-testid={`vencimento-recebivel-${c.id}`}
                    className={`flex items-center justify-between px-4 py-3 rounded-lg border transition-colors ${vencida ? "bg-red-50/60 border-red-100 hover:bg-red-50" : "bg-emerald-50/60 border-emerald-100 hover:bg-emerald-50"}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">{c.descricao}</p>
                      <p className="text-xs text-slate-500 truncate">{c.pagador || c.categoria} · venc. {formatDate(c.vencimento)}</p>
                    </div>
                    <div className="text-right ml-3">
                      <span className={`text-[11px] font-semibold ${vencida ? "text-red-600" : "text-emerald-600"}`}>{vencida ? "Vencido" : "A receber"}</span>
                      <p className="text-sm font-bold text-slate-700">{formatBRL(c.valor)}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Consumos recentes */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-100 shadow-sm">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <h2 className="font-semibold text-slate-800 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-blue-600" /> Consumos Recentes
            </h2>
            <Link to="/consumo" className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
              Ver todos <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="divide-y divide-slate-50">
            {recentes.map((c) => {
              const prod = getProduto(c.produtoId);
              const res = getResidente(c.residenteId);
              return (
                <div key={c.id} className="flex items-center justify-between px-5 py-3.5">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center text-xs font-semibold text-slate-500">
                      {res?.nome?.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-800">{res?.nome}</p>
                      <p className="text-xs text-slate-400">{prod?.nome} · {c.quantidade} {prod?.unidade}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    {c.qtdEmpresa > 0 ? (
                      <span className="text-xs font-semibold text-violet-600 bg-violet-50 px-2 py-1 rounded-md">
                        {formatBRL(c.valorCobrado)}
                      </span>
                    ) : (
                      <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md">
                        Fam. familiar
                      </span>
                    )}
                    <p className="text-[11px] text-slate-400 mt-1">{formatDate(c.data)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Estoque baixo */}
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm">
          <div className="px-5 py-4 border-b border-slate-100">
            <h2 className="font-semibold text-slate-800 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" /> Alerta de Estoque
            </h2>
          </div>
          <div className="p-3 space-y-2">
            {estoqueBaixo.length === 0 && (
              <p className="text-sm text-slate-400 px-2 py-4 text-center">Nenhum produto abaixo do mínimo. 🎉</p>
            )}
            {estoqueBaixo.map((p) => (
              <div key={p.id} className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-amber-50/60">
                <div>
                  <p className="text-sm font-medium text-slate-700">{p.nome}</p>
                  <p className="text-xs text-slate-400">mín. {p.estoqueMinimo} {p.unidade}</p>
                </div>
                <span className="text-sm font-bold text-amber-600">{estoqueEmpresa[p.id] || 0}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
};

export default Dashboard;
